import React, { useEffect, useMemo, useState } from 'react';

import { MenuItemDataType, UserMenuItemDataType } from 'application/providers/MenuItemsProvider/types';
import { OnCertSubmitType } from 'application/components/modals/CertificateModal';
import AnalyticsService, { EventNameAMP } from 'application/services/AnalyticsService';
import FileUtils from 'application/utils/FileUtils';
import { useAccountCompanyId } from 'application/hooks';

import { useCreateCertificate } from '@entities/certificate';
import { toast } from '@shared/ui';
import { DEFAULT_ERROR_MESSAGE } from '@shared/config';

import { ModalTypes, useModalContext } from '../ModalProvider';
import { MenuItemsContext } from './MenuItemsContext';

const MenuItemsProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const companyId = useAccountCompanyId();

  const { openModal, closeModal } = useModalContext();

  const [mainMenuItems, setMainMenuItems] = useState<MenuItemDataType[]>([]);
  const [userMenuItems, setUserMenuItems] = useState<UserMenuItemDataType[]>([]);

  const { request: certificateRequest } = useCreateCertificate();

  const mainMenu = useMemo(
    () => ({
      items: mainMenuItems,
      setItems: setMainMenuItems,
    }),
    [mainMenuItems]
  );

  const userMenu = useMemo(
    () => ({
      items: userMenuItems,
      setItems: setUserMenuItems,
    }),
    [userMenuItems]
  );

  const onCertModalSubmit = async ({ pointIds, createForAll }: OnCertSubmitType) => {
    closeModal();

    const input = {
      companyId,
      pointIds,
      createForAll,
    };

    try {
      const archiveUrl = await certificateRequest(input);
      FileUtils.downloadExternalFile(archiveUrl.data || '');
    } catch (e) {
      toast.error(DEFAULT_ERROR_MESSAGE);
    }
  };

  const showCertModal = () => {
    AnalyticsService.event(EventNameAMP.CERTIFICATE_MENU_ITEM_CLICKED);

    openModal(ModalTypes.CERTIFICATE, {
      onSubmit: onCertModalSubmit,
    });
  };

  useEffect(() => () => closeModal?.(), []);

  return (
    <MenuItemsContext.Provider
      value={{
        mainMenu,
        userMenu,
        showCertModal,
      }}>
      {children}
    </MenuItemsContext.Provider>
  );
};

export default MenuItemsProvider;
